// Modal appears when user clicks on scrape emails Refresh button
// Updated for automated scraping

import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format, formatDistanceToNow } from 'date-fns';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';
import { ApplicationTrackerContext } from '../../contexts/ApplicationTrackerContext';
import { GlobalContext } from '../../contexts/GlobalContext';
import useAchievementTriggers from '../../hooks/useAchievementTriggers';
import LockedFeatureModal from '../common/LockedFeatureModal';

import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider, createTheme } from '@mui/material/styles';

const ScrapeEmailsModal = () => {
  const {
    progress,
    loadScraping,
    isResumingScrape,
    scrapeBackground,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    scrapeEmailsModalOpen,
    setScrapeEmailsModalOpen,
    getScrapingStatus,
    scrapingStatus,
  } = useContext(ApplicationTrackerContext);

  const { trialStatus, trialLoading } = useContext(GlobalContext);
  const checkAchievements = useAchievementTriggers();
  const [selectedPreset, setSelectedPreset] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);

  // Check if user has access to email scraping (not on basic trial)
  const canUseEmailScraping = trialStatus
    ? !trialStatus.has_trial || trialStatus.trial_type === 'full'
    : true;

  // Necessary structure to make DatePicker match light mode theme
  const lightTheme = createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: '#1976d2',
      },
      secondary: {
        main: '#f57c00',
      },
    },
  });

  // Fetch scraping status whenever modal becomes visible
  useEffect(() => {
    // Fetch status when modal opens (scrapeEmailsModalOpen becomes true)
    if (scrapeEmailsModalOpen && getScrapingStatus) {
      const fetchStatus = async () => {
        setStatusLoading(true);
        console.log('Refreshing scraping status as modal opened');
        try {
          await getScrapingStatus();
        } finally {
          setStatusLoading(false);
        }
      };
      fetchStatus();
    }
  }, [scrapeEmailsModalOpen]); // Only re-run when modal opens, not when getScrapingStatus changes

  // Date preset handlers
  const handlePresetClick = (preset) => {
    const today = dayjs();
    setEndDate(today);
    setSelectedPreset(preset);

    switch (preset) {
      case 'week':
        setStartDate(today.subtract(7, 'day'));
        break;
      case 'twoweeks':
        setStartDate(today.subtract(14, 'day'));
        break;
      case 'month':
        setStartDate(today.subtract(30, 'day'));
        break;
      case 'custom':
        setStartDate(null);
        break;
      default:
        break;
    }
  };

  // Minimum allowed date (June 1, 2024)
  const minDate = dayjs('2024-06-01');
  const maxDate = dayjs();

  // Show loading state while fetching status
  if (statusLoading || trialLoading) {
    return (
      <div>
        <div
          id="login-modal"
          className={`flex justify-center items-center bg-black/70 fixed top-0 left-0 w-full h-full z-80 overflow-x-hidden transition-all overflow-y-auto pointer-events-none`}
        >
          <div className="sm:max-w-lg sm:w-full m-3 sm:mx-auto">
            <div className="flex flex-col bg-white border shadow-xs rounded-xl pointer-events-auto">
              <div className="p-8 text-center">
                <div
                  className="animate-spin inline-block size-8 border-[3px] border-current border-t-transparent text-blue-600 rounded-full mb-4"
                  role="status"
                >
                  <span className="sr-only">Loading...</span>
                </div>
                <p className="text-gray-600">Loading scraping status...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check if user has access to email scraping (not on basic trial)
  if (!canUseEmailScraping) {
    return (
      <div>
        <div
          id="login-modal"
          className={`flex justify-center items-center bg-black/70 fixed top-0 left-0 w-full h-full z-80 overflow-x-hidden transition-all overflow-y-auto pointer-events-none`}
        >
          <div className="sm:max-w-lg sm:w-full m-3 sm:mx-auto">
            <div className="flex flex-col bg-white border shadow-xs rounded-xl pointer-events-auto">
              <div className="flex justify-between items-center py-3 px-4 border-b">
                <h3 className="font-bold text-gray-800">Email Scraping</h3>
                <button
                  type="button"
                  onClick={() => {
                    setScrapeEmailsModalOpen(false);
                  }}
                  className="flex justify-center items-center size-7 text-base font-semibold rounded-full border border-transparent text-gray-800 hover:bg-gray-100 disabled:opacity-50 disabled:pointer-events-none"
                >
                  <span className="sr-only">Close</span>
                  <svg
                    className="shrink-0 size-4"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M18 6 6 18"></path>
                    <path d="m6 6 12 12"></path>
                  </svg>
                </button>
              </div>
              <LockedFeatureModal featureName="Automated Email Scraping" showAsInline={true} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check if automated scraping is already active
  if (scrapingStatus?.is_initial_scrape_complete) {
    return (
      <div>
        <div
          id="login-modal"
          className={`flex justify-center items-center bg-black/70 fixed top-0 left-0 w-full h-full z-80 overflow-x-hidden transition-all overflow-y-auto pointer-events-none`}
        >
          <div className="sm:max-w-lg sm:w-full m-3 sm:mx-auto">
            <div className="flex flex-col bg-white border shadow-xs rounded-xl pointer-events-auto">
              <div className="flex justify-between items-center py-3 px-4 border-b">
                <h3 className="font-bold text-gray-800">✨ Automated Email Scraping Active ✨</h3>
                <button
                  type="button"
                  onClick={() => {
                    setScrapeEmailsModalOpen(false);
                  }}
                  className="flex justify-center items-center size-7 text-base font-semibold rounded-full border border-transparent text-gray-800 hover:bg-gray-100 disabled:opacity-50 disabled:pointer-events-none"
                >
                  <span className="sr-only">Close</span>
                  <svg
                    className="shrink-0 size-4"
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M18 6 6 18"></path>
                    <path d="m6 6 12 12"></path>
                  </svg>
                </button>
              </div>

              <div className="p-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                  <p className="text-green-800 font-medium mb-2">
                    ✅ Automated scraping is active for your account
                  </p>
                  <p className="text-green-700 text-sm">
                    Your emails are being automatically checked every hour for new job application
                    updates.
                  </p>
                </div>

                <div className="space-y-2 text-sm text-gray-600">
                  {scrapingStatus.email && (
                    <p>
                      <strong>Connected email:</strong> {scrapingStatus.email}
                    </p>
                  )}
                  {scrapingStatus.last_scraped_date && (
                    <p>
                      <strong>Last scraped:</strong>{' '}
                      {formatDistanceToNow(new Date(scrapingStatus.last_scraped_date))} ago
                    </p>
                  )}
                  {scrapingStatus.next_scrape_date && (
                    <p>
                      <strong>Next scrape:</strong>{' '}
                      {format(new Date(scrapingStatus.next_scrape_date), 'MMM dd, yyyy HH:mm')}
                    </p>
                  )}
                  {scrapingStatus.initial_scrape_start_date &&
                    scrapingStatus.initial_scrape_end_date && (
                      <p>
                        <strong>Initial scrape range:</strong>{' '}
                        {format(new Date(scrapingStatus.initial_scrape_start_date), 'MMM dd, yyyy')}{' '}
                        - {format(new Date(scrapingStatus.initial_scrape_end_date), 'MMM dd, yyyy')}
                      </p>
                    )}
                </div>

                <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-blue-800 text-sm">
                    💡 <strong>Tip:</strong> No action needed! Your emails are being monitored
                    automatically. New applications will appear in your tracker within an hour of
                    receiving the email.
                  </p>
                </div>
              </div>

              <div className="flex justify-end p-4 border-t">
                <button
                  type="button"
                  onClick={() => setScrapeEmailsModalOpen(false)}
                  className="py-2 px-4 text-sm font-semibold rounded-lg border border-gray-300 bg-white text-gray-800 hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Initial setup view (or resuming view)
  return (
    <div>
      <div
        id="login-modal"
        className={`flex justify-center items-center bg-black/70 fixed top-0 left-0 w-full h-full z-80 overflow-x-hidden transition-all overflow-y-auto pointer-events-none`}
      >
        <div className="sm:max-w-lg sm:w-full m-3 sm:mx-auto">
          <div className="flex flex-col bg-white border shadow-xs rounded-xl pointer-events-auto">
            <div className="flex justify-between items-center py-3 px-4 border-b">
              <h3 className="font-bold text-gray-800">
                {isResumingScrape
                  ? '📧 Email Scan in Progress...'
                  : '✨ Set Up Automated Application Tracking ✨'}
              </h3>
              <button
                type="button"
                onClick={() => {
                  setScrapeEmailsModalOpen(false);
                }}
                className="flex justify-center items-center size-7 text-base font-semibold rounded-full border border-transparent text-gray-800 hover:bg-gray-100 disabled:opacity-50 disabled:pointer-events-none"
              >
                <span className="sr-only">Close</span>
                <svg
                  className="shrink-0 size-4"
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M18 6 6 18"></path>
                  <path d="m6 6 12 12"></path>
                </svg>
              </button>
            </div>

            <div className="flex flex-col justify-center items-center py-3 px-4">
              {!isResumingScrape && (
                <>
                  <h2 className="mb-3 text-center text-lg font-semibold">
                    One-time setup: Select how far back to scan for applications
                  </h2>
                  <p className="text-center text-sm text-gray-600 mb-4">
                    After this initial scan, your emails will be checked automatically every hour
                    for new applications.
                  </p>
                </>
              )}
              {isResumingScrape && (
                <>
                  <h2 className="mb-3 text-center text-lg font-semibold">
                    Resuming your email scan
                  </h2>
                  <p className="text-center text-sm text-gray-600 mb-4">
                    We found a scan that was in progress. Picking up where we left off...
                  </p>
                </>
              )}

              {/* Date range preset buttons */}
              {!isResumingScrape && (
                <>
                  <div className="w-full mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Quick select:</p>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => handlePresetClick('week')}
                        className={`py-2 px-3 text-sm font-medium rounded-lg border ${
                          selectedPreset === 'week'
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        Last week
                      </button>
                      <button
                        onClick={() => handlePresetClick('twoweeks')}
                        className={`py-2 px-3 text-sm font-medium rounded-lg border ${
                          selectedPreset === 'twoweeks'
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        Last 2 weeks
                      </button>
                      <button
                        onClick={() => handlePresetClick('month')}
                        className={`py-2 px-3 text-sm font-medium rounded-lg border ${
                          selectedPreset === 'month'
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        Last month
                      </button>
                      <button
                        onClick={() => handlePresetClick('custom')}
                        className={`py-2 px-3 text-sm font-medium rounded-lg border ${
                          selectedPreset === 'custom'
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        Custom range
                      </button>
                    </div>
                  </div>

                  {/* Date pickers */}
                  <div className="w-full space-y-3 mb-4">
                    <ThemeProvider theme={lightTheme}>
                      <CssBaseline />
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DatePicker
                          label="Start date"
                          value={startDate}
                          onChange={(newValue) => {
                            setStartDate(newValue);
                            setSelectedPreset('custom');
                          }}
                          minDate={minDate}
                          maxDate={maxDate}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              helperText: 'Earliest: June 1, 2024',
                            },
                          }}
                        />
                        <DatePicker
                          label="End date"
                          value={endDate}
                          onChange={(newValue) => {
                            setEndDate(newValue);
                            setSelectedPreset('custom');
                          }}
                          minDate={startDate || minDate}
                          maxDate={maxDate}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </ThemeProvider>
                  </div>
                </>
              )}

              {/* Progress indicator */}
              {progress !== 0 && progress !== 1 && (
                <div className="w-full mb-3">
                  <p className="text-sm text-gray-600 mb-1">
                    {isResumingScrape
                      ? `Resuming email scan... ${Math.floor(progress * 100)}%`
                      : `Scanning your emails... ${Math.floor(progress * 100)}%`}
                  </p>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                      style={{ width: `${Math.floor(progress * 100)}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Info box */}
              <div className="w-full bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                <p className="text-blue-800 text-sm">
                  <strong>Note:</strong> You can close this window after starting. The scan will
                  continue in the background and your applications will be added automatically.
                </p>
              </div>
            </div>

            {/* Start button */}
            {!isResumingScrape && (
              <div className="p-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    console.log('Starting email scrape');
                    scrapeBackground();
                  }}
                  disabled={!startDate || !endDate || loadScraping}
                  className={`w-full py-2 px-3 inline-flex justify-center items-center gap-x-2 text-base font-semibold rounded-lg border border-transparent bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:pointer-events-none`}
                >
                  {loadScraping ? (
                    <>
                      <span
                        className="animate-spin inline-block size-4 border-[3px] border-current border-t-transparent text-white rounded-full"
                        role="status"
                        aria-label="loading"
                      >
                        <span className="sr-only">Loading...</span>
                      </span>
                      Scanning emails...
                    </>
                  ) : (
                    'Start Initial Scan & Enable Automation'
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScrapeEmailsModal;
